import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { DistrictNav } from '../district-nav/district-nav';
import { IssueReport, ReportCategory, ReportStatus } from '../../../../models/reports';
import { StatusStepper } from '../../../common/status-stepper/status-stepper';
import { IssueReportService } from '../../../../services/reports.service';
import { IssueReportsInfo } from './issue-reports-info/issue-reports-info';

const CATEGORY_ICON: Record<ReportCategory, string> = {
  Bug: 'bug_report',
  Outage: 'flash_off',
  'Feature Request': 'lightbulb',
  'Access Issue': 'key',
  Other: 'chat_bubble',
};

const CATEGORY_CLASS: Record<ReportCategory, string> = {
  Bug: 'bug',
  Outage: 'outage',
  'Feature Request': 'feature',
  'Access Issue': 'access',
  Other: 'other',
};

const STATUS_CLASS: Record<ReportStatus, string> = {
  Submitted: 'submitted',
  Acknowledged: 'acknowledged',
  'In Progress': 'progress',
  Resolved: 'resolved',
};

type FilterValue = 'All' | 'Open' | 'Resolved';

@Component({
  selector: 'app-district-reports',
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatChipsModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatDialogModule,
    DistrictNav,
    StatusStepper,
  ],
  templateUrl: './district-reports.html',
  styleUrl: './district-reports.css',
})
export class DistrictReports {
  reportService = inject(IssueReportService);
  dialog = inject(MatDialog);

  today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  reports = signal<IssueReport[]>([]);
  filter = signal<FilterValue>('All');
  query = signal('');

  displayedColumns = ['id', 'title', 'category', 'priority', 'status', 'assignedTo', 'actions'];

  openCount = computed(() => this.reports().filter((r) => r.status !== 'Resolved').length);

  inProgressCount = computed(() => this.reports().filter((r) => r.status === 'In Progress').length);

  resolvedCount = computed(() => this.reports().filter((r) => r.status === 'Resolved').length);

  filteredReports = computed(() => {
    const f = this.filter();
    const q = this.query().trim().toLowerCase();

    return this.reports().filter((r) => {
      const matchesFilter =
        f === 'All' ||
        (f === 'Open' && r.status !== 'Resolved') ||
        (f === 'Resolved' && r.status === 'Resolved');

      const matchesQuery =
        q === '' || r.title.toLowerCase().includes(q) || r.school!.name.toLowerCase().includes(q);

      return matchesFilter && matchesQuery;
    });
  });

  totalOpen = computed(() => this.reports().filter((r) => r.status !== 'Resolved').length);
  totalInProgress = computed(() => this.reports().filter((r) => r.status === 'In Progress').length);
  totalResolved = computed(() => this.reports().filter((r) => r.status === 'Resolved').length);

  ngOnInit() {
    this.reportService.getReports().subscribe((reports) => {
      this.reports.set(reports);
      console.log(this.reports());
    });
  }

  setFilter(value: FilterValue): void {
    this.filter.set(value);
  }

  categoryIcon(category: ReportCategory): string {
    return CATEGORY_ICON[category];
  }

  categoryClass(category: ReportCategory): string {
    return CATEGORY_CLASS[category];
  }

  statusClass(status: ReportStatus): string {
    return STATUS_CLASS[status];
  }

  initials(name: string): string {
    return name
      .split(' ')
      .filter((part) => /[A-Za-z]/.test(part[0]))
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }

  openReportDialog(report: IssueReport): void {
    const ref = this.dialog.open(IssueReportsInfo, {
      width: '640px',
      maxWidth: '95vw',
      data: {
        report,
        currentUserId: 1,
      },
      autoFocus: false,
    });
    // Capture the change flag while the component still exists (beforeClosed),
    // then refresh after it closes. This works no matter how the dialog is
    // dismissed — close button, backdrop click, or Escape — not only when
    // close() runs and returns a payload.
    let changed = false;
    ref.beforeClosed().subscribe(() => {
      changed = ref.componentInstance?.reportChanged ?? false;
    });
    ref.afterClosed().subscribe(() => {
      if (changed) {
        this.reportService.getReports().subscribe((reports) => this.reports.set(reports));
      }
    });
  }
}
